// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Trash2, Plus, Minus, ShoppingBag, Tag, Truck, Package, X, ArrowRight } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

function getProductEmoji(name = '') {
  const n = name.toLowerCase();
  if (n.includes('banana'))  return '🍌'; if (n.includes('apple'))  return '🍎';
  if (n.includes('spinach') || n.includes('lettuce')) return '🥬';
  if (n.includes('avocado')) return '🥑'; if (n.includes('tomato')) return '🍅';
  if (n.includes('milk'))    return '🥛'; if (n.includes('egg'))    return '🥚';
  if (n.includes('yogurt'))  return '🫙'; if (n.includes('cheese')) return '🧀';
  if (n.includes('chicken')) return '🍗';
  if (n.includes('beef') || n.includes('ground')) return '🥩';
  if (n.includes('salmon'))  return '🐟'; if (n.includes('bread') || n.includes('sourdough')) return '🍞';
  if (n.includes('croissant')) return '🥐'; if (n.includes('pasta')) return '🍝';
  if (n.includes('sauce'))   return '🫙'; if (n.includes('olive'))  return '🫒';
  if (n.includes('water'))   return '💧'; if (n.includes('juice'))  return '🍊';
  if (n.includes('coffee'))  return '☕';
  return '🛒';
}

export default function CartPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    items, updateQuantity, removeItem,
    subtotal, discount, deliveryFee, tax, total, itemCount,
    coupon, applyCoupon, removeCoupon,
    fulfillment, setFulfillment,
  } = useCart();

  const [couponCode, setCouponCode] = React.useState('');
  const [couponError, setCouponError] = React.useState('');
  const [couponLoading, setCouponLoading] = React.useState(false);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponError(''); setCouponLoading(true);
    try {
      await applyCoupon(couponCode.trim().toUpperCase());
      setCouponCode('');
    } catch (err) {
      setCouponError(err.message);
    } finally {
      setCouponLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="page-container" style={{ paddingTop: 64, textAlign: 'center' }}>
        <div style={{ fontSize: 72, marginBottom: 20 }}>🛒</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: '#14532D', marginBottom: 10 }}>Your cart is empty</h2>
        <p style={{ fontSize: 15, color: '#64748B', marginBottom: 28 }}>
          Add some fresh groceries to get started!
        </p>
        <Link to="/products" style={{ textDecoration: 'none' }}>
          <button className="btn btn-primary btn-lg">
            <ShoppingBag size={18} /> Shop Now
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <h1 style={{ fontSize: 28, fontWeight: 900, color: '#14532D', marginBottom: 28 }}>
        Your Cart <span style={{ fontSize: 16, color: '#64748B', fontWeight: 500 }}>({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
      </h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 32, alignItems: 'start' }}>
        {/* Items list */}
        <div>
          {/* Fulfillment toggle */}
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#14532D', marginBottom: 12 }}>How would you like to receive your order?</div>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { key: 'delivery', label: 'Delivery', icon: <Truck size={16} />, sub: 'Est. 2–4 hours' },
                { key: 'pickup',   label: 'Pickup',   icon: <Package size={16} />, sub: 'Ready in 1 hour' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setFulfillment(opt.key)}
                  style={{
                    flex: 1, padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                    border: fulfillment === opt.key ? '2px solid #16A34A' : '2px solid #E2E8F0',
                    background: fulfillment === opt.key ? '#F0FDF4' : '#fff',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{ color: fulfillment === opt.key ? '#16A34A' : '#94A3B8' }}>{opt.icon}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: fulfillment === opt.key ? '#14532D' : '#64748B' }}>{opt.label}</span>
                  <span style={{ fontSize: 11, color: '#94A3B8' }}>{opt.sub}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Cart items */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {items.map((item, idx) => (
              <div
                key={item.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px',
                  borderBottom: idx < items.length - 1 ? '1px solid #F0FDF4' : 'none',
                }}
              >
                {/* Emoji image */}
                <div style={{
                  width: 64, height: 64, borderRadius: 12,
                  background: 'linear-gradient(135deg, #F0FDF4, #DCFCE7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 32, flexShrink: 0,
                }}>
                  {getProductEmoji(item.product_name)}
                </div>

                {/* Name + unit */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#14532D', marginBottom: 2 }}>
                    {item.product_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>{item.unit}</div>
                  {item.sale_price_cents && item.sale_price_cents < item.price_cents ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginTop: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: '#EF4444' }}>{fmt(item.sale_price_cents)}</span>
                      <span style={{ fontSize: 12, color: '#94A3B8', textDecoration: 'line-through' }}>{fmt(item.price_cents)}</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#14532D', marginTop: 2 }}>{fmt(item.price_cents)}</div>
                  )}
                </div>

                {/* Qty controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    onClick={() => item.quantity <= 1 ? removeItem(item.id) : updateQuantity(item.id, item.quantity - 1)}
                    style={{ width: 30, height: 30, borderRadius: 8, background: '#F0FDF4', border: '1px solid #BBF7D0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {item.quantity <= 1 ? <Trash2 size={13} color="#EF4444" /> : <Minus size={13} color="#16A34A" />}
                  </button>
                  <span style={{ fontSize: 15, fontWeight: 700, minWidth: 20, textAlign: 'center', color: '#14532D' }}>{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    style={{ width: 30, height: 30, borderRadius: 8, background: '#16A34A', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Plus size={13} color="#fff" />
                  </button>
                </div>

                {/* Line total */}
                <div style={{ minWidth: 64, textAlign: 'right' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#14532D' }}>
                    {fmt((item.sale_price_cents || item.price_cents) * item.quantity)}
                  </div>
                </div>

                {/* Remove */}
                <button
                  onClick={() => removeItem(item.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CBD5E1', padding: 4 }}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* Continue shopping */}
          <div style={{ marginTop: 16 }}>
            <Link to="/products" style={{ textDecoration: 'none' }}>
              <button className="btn btn-ghost btn-sm" style={{ color: '#16A34A' }}>
                ← Continue Shopping
              </button>
            </Link>
          </div>
        </div>

        {/* Order summary */}
        <div style={{ position: 'sticky', top: 80 }}>
          <div className="card" style={{ padding: 24 }}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#14532D', marginBottom: 20 }}>Order Summary</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#64748B' }}>
                <span>Subtotal ({itemCount} items)</span>
                <span>{fmt(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#16A34A', fontWeight: 600 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Tag size={13} /> Coupon discount
                  </span>
                  <span>−{fmt(discount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#64748B' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Truck size={13} /> {fulfillment === 'pickup' ? 'Pickup' : 'Delivery'}
                </span>
                <span style={{ color: deliveryFee === 0 ? '#16A34A' : undefined }}>
                  {deliveryFee === 0 ? (fulfillment === 'pickup' ? 'Free' : 'Free') : fmt(deliveryFee)}
                </span>
              </div>
              {fulfillment === 'delivery' && deliveryFee > 0 && (
                <div style={{ fontSize: 11, color: '#94A3B8', textAlign: 'right' }}>
                  Free delivery on orders over $75
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#64748B' }}>
                <span>Tax (7%)</span>
                <span>{fmt(tax)}</span>
              </div>
              <div style={{ height: 1, background: '#F0FDF4', margin: '4px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 900, color: '#14532D' }}>
                <span>Total</span>
                <span>{fmt(total)}</span>
              </div>
            </div>

            {/* Coupon input */}
            {!coupon ? (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748B', marginBottom: 8 }}>Have a coupon code?</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="input"
                    style={{ flex: 1, minHeight: 38, fontSize: 13, textTransform: 'uppercase' }}
                    placeholder="Enter code"
                    value={couponCode}
                    onChange={e => { setCouponCode(e.target.value); setCouponError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                  />
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {couponLoading ? '...' : 'Apply'}
                  </button>
                </div>
                {couponError && (
                  <div style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{couponError}</div>
                )}
              </div>
            ) : (
              <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Tag size={14} color="#16A34A" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#15803D' }}>{coupon.code}</span>
                  <span style={{ fontSize: 12, color: '#64748B' }}>applied</span>
                </div>
                <button onClick={removeCoupon} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}>
                  <X size={14} />
                </button>
              </div>
            )}

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              onClick={() => user ? navigate('/checkout') : navigate('/login')}
            >
              {user ? (
                <>Proceed to Checkout <ArrowRight size={16} /></>
              ) : (
                <>Sign In to Checkout <ArrowRight size={16} /></>
              )}
            </button>

            {!user && (
              <div style={{ fontSize: 12, color: '#94A3B8', textAlign: 'center', marginTop: 10 }}>
                <Link to="/login" style={{ color: '#16A34A', fontWeight: 600 }}>Sign in</Link> or{' '}
                <Link to="/login" style={{ color: '#16A34A', fontWeight: 600 }}>create an account</Link> to earn loyalty points
              </div>
            )}

            {/* Trust badges */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 20, paddingTop: 16, borderTop: '1px solid #F0FDF4' }}>
              {['🔒 Secure', '↩ Easy Returns', '⭐ Earn Points'].map(b => (
                <div key={b} style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center' }}>{b}</div>
              ))}
            </div>
          </div>

          {/* Suggested coupons */}
          <Link to="/coupons" style={{ textDecoration: 'none' }}>
            <div style={{ marginTop: 12, background: '#FFF7ED', borderRadius: 12, padding: '12px 16px', border: '1px solid #FED7AA', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag size={16} color="#F97316" />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#C2410C' }}>See available coupons</span>
              </div>
              <ArrowRight size={14} color="#F97316" />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
